// 메시지 리스트 — 바닐라 #messages 영역 이식. 마크업 클래스를 바닐라와 동일하게
// 유지해 innerHTML 이 그대로 스냅샷(messagesHtml)이 되고, 바닐라 openSession 이
// 복원해도 위임 클릭이 동작한다. 반대로 바닐라가 저장한 messagesHtml 은
// restoredHtml 로 그대로 흘리고(va-restored 래퍼, display:contents) 클릭은
// 컨테이너 위임으로 처리한다 (바닐라 #messages 위임과 동일 방식).
import { memo, useEffect, useState } from "react";
import { fileFace, type FileFace } from "../../lib/fileFace";
import { libToast } from "../../lib/library";
import { fmtElapsed, type AgentTurn, type ChatTurn, type ProgressLine } from "./types";
import { BrandMark } from "../../ui/BrandMark";
import { Chip } from "../../ui/Chip";

/** 실행 중 경과 시간 — 1초마다 자체 갱신 (부모 커밋과 무관하게 틱).
 *  긴 턴에서 "멈춘 건가?" 불안을 줄이는 게 목적이라 초 단위면 충분하다. */
function Elapsed({ since }: { since: number }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return <>{fmtElapsed(Date.now() - since)}</>;
}

/** dangerouslySetInnerHTML 래퍼 — memo 로 html 이 같으면 재렌더하지 않는다.
 *  재렌더 시 React 가 innerHTML 을 다시 적용해, 위임 클릭으로 지운/토글한
 *  DOM(꼬리질문 칩 제거·표 펼침·더 보기)이 "부활"하던 문제의 방지책. */
const RawHtml = memo(function RawHtml({
  className,
  html,
  contents,
  quiet,
  attrs,
}: {
  className: string;
  html: string;
  /** display:contents (복원 스냅샷 래퍼 — 레이아웃 중립, 스냅샷에도 실림) */
  contents?: boolean;
  /** 표시 없이 스냅샷에만 남기는 아티팩트 (download quiet) */
  quiet?: boolean;
  /** 컨테이너 div 추가 속성 (screen_panel 의 data-spec — 스냅샷 영속) */
  attrs?: Record<string, string>;
}) {
  return (
    <div
      className={className}
      {...(attrs || {})}
      style={contents ? { display: "contents" } : quiet ? { display: "none" } : undefined}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
});

interface Props {
  turns: ChatTurn[];
  /** 세션 복원 스냅샷 HTML (바닐라·React 공용) — 새 턴은 이 뒤에 쌓인다 */
  restoredHtml: string;
  busy: boolean;
  listRef: React.RefObject<HTMLDivElement | null>;
  onSend: (text: string) => void;
  onToggleProgress: (turnIdx: number) => void;
  onPickChoice: (turnIdx: number, text: string) => void;
  onPickNext: (turnIdx: number, text: string) => void;
  /** screen_panel 힌트 바 클릭 — 그 결과 세트(DOM 순서 인덱스)를 골라 패널 오픈 */
  onPanelOpen?: (specIdx: number) => void;
  /** 다운로드 아티팩트의 '미리보기' 버튼 — 우측 파일 미리보기 패널 오픈 */
  onPreviewOpen?: (f: { url: string; name: string; kind: string }) => void;
  /** 업로드한 첨부의 URL 조회 (uploads SSE 로 받은 이름→URL) */
  uploadUrlOf?: (name: string) => string | undefined;
  /** 첨부 칩 클릭 — 표 형식만 호출된다 */
  onOpenUpload?: (url: string, name: string) => void;
}

// 첨부 칩 아이콘 — React 채팅에는 lucide 가 없어 인라인 SVG 로 둔다.
// 바닐라(i[data-lucide])와 시각적으로 같은 계열을 쓴다.
const ICON_PATHS: Record<FileFace["icon"], string> = {
  sheet: "M3 3h18v18H3z M3 9h18 M3 15h18 M9 3v18 M15 3v18",
  "file-text": "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M8 13h8 M8 17h5",
  "file-type": "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M9 13h6 M12 13v5",
  presentation: "M2 3h20 M4 3v11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V3 M12 15v6 M8 21h8",
  image: "M3 3h18v18H3z M8.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z M21 15l-5-5L5 21",
  paperclip: "M21.4 11.1l-8.5 8.5a5 5 0 0 1-7-7l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4l7.8-7.8",
};

function FileIcon({ icon }: { icon: FileFace["icon"] }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICON_PATHS[icon].split(" M").map((d, i) => (
        <path key={i} d={i === 0 ? d : "M" + d} />
      ))}
    </svg>
  );
}

export function DsMark({ loading, className }: { loading?: boolean; className?: string }) {
  return (
    <BrandMark className={className || "agent-ico ds-mark"} loading={loading} />
  );
}

const SEARCH_SVG = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </svg>
);

const COPY_SVG = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

/** 메시지 하단 메타 줄 — 복사 버튼 + 시각. 클릭은 컨테이너 위임(.msg-copy)이
 *  처리하므로 자체 onClick 이 없다 — 복원 스냅샷 안의 버튼도 똑같이 동작한다. */
function MsgMeta({ time, label }: { time: string; label: string }) {
  return (
    <div className="msg-meta">
      <button type="button" className="msg-copy" title={label} aria-label={label}>
        {COPY_SVG}
      </button>
      {time ? <span className="msg-meta-time">{time}</span> : null}
    </div>
  );
}

function lineLead(l: ProgressLine, showSpinner: boolean) {
  if (l.kind === "spin")
    return showSpinner ? <span className="spinner">{SEARCH_SVG}</span> : null;
  if (l.kind === "ok") return "✓ ";
  if (l.kind === "tool") return "▸ ";
  return "· ";
}

function lineClass(l: ProgressLine): string {
  return (
    "progress-line" + (l.kind === "tool" ? " tool" : l.kind === "ok" ? " done-ok" : "")
  );
}

function ProgressLog({
  turn,
  onToggle,
}: {
  turn: AgentTurn;
  onToggle: () => void;
}) {
  const p = turn.progress;
  if (!p.history.length && !p.current) return <div className="progress-log" data-log />;
  return (
    <div className="progress-log" data-log>
      {p.history.length > 0 && (
        <>
          <button
            className="progress-toggle"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            {p.open ? "이전 단계 접기 ▴" : `이전 단계 보기 (${p.history.length}개) ▾`}
          </button>
          <div className="progress-history" hidden={!p.open}>
            {p.history.map((l, k) => (
              <div key={k} className={lineClass(l)}>
                {lineLead(l, false)}
                <span>{l.text}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {p.current && (
        <div
          className={
            p.finalized
              ? "progress-line done-ok"
              : lineClass(p.current) + " progress-current"
          }
        >
          {lineLead(p.current, !p.finalized && !turn.streaming)}
          <span>{p.current.text}</span>
        </div>
      )}
    </div>
  );
}

export default function MessageList({
  turns,
  restoredHtml,
  busy,
  listRef,
  onSend,
  onToggleProgress,
  onPickChoice,
  onPickNext,
  onPanelOpen,
  onPreviewOpen,
  uploadUrlOf,
  onOpenUpload,
}: Props) {
  // 복원 HTML·아티팩트 HTML 안 요소들의 클릭 처리 (위임 — 바닐라 #messages 위임 이식).
  // React 가 렌더한 칩·토글은 자체 onClick 에서 stopPropagation 하므로 여기 오지 않는다.
  const onListClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    // 메시지 복사 — React 렌더·복원 스냅샷 공용 (위임 하나로 둘 다 처리)
    const copyBtn = target.closest(".msg-copy") as HTMLElement | null;
    if (copyBtn) {
      const msg = copyBtn.closest(".message");
      const src = (msg?.querySelector(".message-flow") ||
        msg?.querySelector(".message-text")) as HTMLElement | null;
      const txt = (src?.innerText || "").trim();
      if (!txt) return;
      void navigator.clipboard?.writeText(txt).then(
        () => libToast("메시지를 복사했습니다."),
        () => libToast("복사에 실패했습니다 — 브라우저 권한을 확인해 주세요."),
      );
      return;
    }
    // 복원 스냅샷(바닐라가 저장한 messagesHtml) 안의 첨부 칩 — React 가 그린 칩은
    // 자체 onClick 에서 stopPropagation 하므로 여기로 오지 않는다.
    const fileChip = target.closest(".msg-file.tabular") as HTMLElement | null;
    if (fileChip) {
      const name = fileChip.dataset.fname || (fileChip.textContent || "").trim();
      const url = uploadUrlOf?.(name);
      if (url && onOpenUpload) onOpenUpload(url, name);
      // 지난 대화의 업로드는 토큰이 만료(24h)돼 URL 이 없다 — 왜 안 열리는지 알려준다
      else libToast("이 첨부는 이번 대화에서 다시 올려야 열 수 있습니다.");
      return;
    }
    // 다운로드 아티팩트의 '미리보기' 버튼 → 우측 파일 미리보기 패널 오픈.
    // 인라인 onclick 없이 위임만으로 동작 — 스냅샷 복원 HTML 의 버튼도 여기로 온다.
    const pvBtn = target.closest("[data-preview-url]") as HTMLElement | null;
    if (pvBtn && onPreviewOpen) {
      const url = pvBtn.dataset.previewUrl || "";
      if (url)
        onPreviewOpen({
          url,
          name: pvBtn.dataset.previewName || "문서",
          kind: pvBtn.dataset.previewKind || "file",
        });
      return;
    }
    // screen_panel 힌트 바 → 그 결과 세트를 골라 우측 결과 패널 오픈
    const panelBtn = target.closest("[data-panel-open]") as HTMLElement | null;
    if (panelBtn && onPanelOpen) {
      const list = e.currentTarget;
      const nodes = Array.from(list.querySelectorAll(".screen-panel-data"));
      const idx = nodes.indexOf(panelBtn.closest(".screen-panel-data") as Element);
      onPanelOpen(idx);
      return;
    }
    // 표 아티팩트 펼치기/접기
    const tblToggle = target.closest(".art-table-toggle") as HTMLElement | null;
    if (tblToggle) {
      const list = tblToggle.parentElement?.querySelector(
        ".art-table-list",
      ) as HTMLElement | null;
      if (!list) return;
      const open = list.hidden;
      list.hidden = !open;
      tblToggle.setAttribute("aria-expanded", String(open));
      const cnt = tblToggle.querySelector(".art-table-toggle-count");
      if (cnt)
        cnt.textContent = open
          ? "닫기 ▴"
          : `${list.querySelectorAll(".art-row").length}개 결과 보기 ▾`;
      return;
    }
    // '더 보기' → 숨긴 행 10개씩 추가 노출
    const moreBtn = target.closest(".art-more") as HTMLElement | null;
    if (moreBtn) {
      const list = moreBtn.closest(".art-table-list");
      if (!list) return;
      [...list.querySelectorAll(".art-row[hidden]")]
        .slice(0, 10)
        .forEach((r) => r.removeAttribute("hidden"));
      const remain = list.querySelectorAll(".art-row[hidden]").length;
      const total = list.querySelectorAll(".art-row").length;
      if (remain) moreBtn.textContent = `더 보기 (${total - remain}/${total})`;
      else moreBtn.remove();
      return;
    }
    // 표 행 클릭 → 그 항목으로 이어서 질문
    const row = target.closest(".art-row") as HTMLElement | null;
    if (row && row.dataset.send) {
      if (busy) return;
      const list = row.closest(".art-table-list");
      if (list)
        list.querySelectorAll(".art-row").forEach((r) => r.classList.remove("picked"));
      row.classList.add("picked");
      onSend(row.dataset.send);
      return;
    }
    // 복원 HTML 안의 진행 로그 이전 단계 펼치기/접기
    const progToggle = target.closest(".progress-toggle") as HTMLElement | null;
    if (progToggle) {
      const hist = progToggle.parentElement?.querySelector(
        ".progress-history",
      ) as HTMLElement | null;
      if (!hist) return;
      const open = hist.hasAttribute("hidden");
      if (open) {
        hist.removeAttribute("hidden");
        progToggle.textContent = "이전 단계 접기 ▴";
      } else {
        hist.setAttribute("hidden", "");
        progToggle.textContent = `이전 단계 보기 (${hist.children.length}개) ▾`;
      }
      return;
    }
    // 복원 HTML 안의 꼬리 질문·선택지 칩
    const nextChip = target.closest(".next-box .next-chip") as HTMLElement | null;
    if (nextChip) {
      if (busy) return;
      const text = (nextChip.textContent || "").trim();
      nextChip.closest(".next-box")?.remove();
      if (text) onSend(text);
      return;
    }
    const chip = target.closest(".choice-box .choice-chip") as HTMLElement | null;
    if (chip) {
      if (busy) return;
      const text = (chip.textContent || "").trim();
      chip.closest(".choice-box")?.remove();
      if (text) onSend(text);
    }
  };

  return (
    <div className="message-list" id="messages" ref={listRef} onClick={onListClick}>
      {restoredHtml ? (
        <RawHtml className="va-restored" html={restoredHtml} contents />
      ) : null}
      {turns.map((t, i) =>
        t.role === "user" ? (
          <article className="message user" key={i}>
            <div className="message-role">USER</div>
            <div>
              <div className="message-head">
                <span className="message-time">{t.time}</span>
              </div>
              {/* 첨부는 질문 위에 — "이 파일 뭐야"를 읽기 전에 무엇을 올렸는지 먼저 보이도록 */}
              {t.files && t.files.length > 0 && (
                <div className="msg-files">
                  {t.files.map((name) => {
                    const k = fileFace(name);
                    const url = uploadUrlOf?.(name);
                    return (
                      <button
                        key={name}
                        type="button"
                        className={`msg-file ${k.cls}${k.tabular ? " tabular" : ""}`}
                        disabled={!k.tabular}
                        title={k.tabular ? `${k.label} — 눌러서 미리보기` : k.label}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (url) onOpenUpload?.(url, name);
                          else libToast("파일을 아직 준비 중입니다 — 잠시 후 다시 눌러 주세요.");
                        }}
                      >
                        <FileIcon icon={k.icon} />
                        {name}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="message-text">{t.text}</div>
              <MsgMeta time={t.time} label="질문 복사" />
            </div>
          </article>
        ) : (
          <article className="message agent" key={i}>
            <div className="message-role">AGENT</div>
            <div>
              <div className="message-head">
                <span className="message-name">
                  <DsMark loading={t.live} />
                  Valuation Agent
                </span>
                <span className="message-time" data-time>
                  {t.live ? (
                    t.startedAt ? (
                      <>
                        working… · <Elapsed since={t.startedAt} />
                      </>
                    ) : (
                      "working…"
                    )
                  ) : (
                    t.time
                  )}
                </span>
              </div>
              <ProgressLog turn={t} onToggle={() => onToggleProgress(i)} />
              <div className="message-flow" data-flow>
                {t.flow.map((f, j) =>
                  f.kind === "text" ? (
                    // RawHtml(memo) — html 이 같으면 재렌더하지 않는다. React 19 는
                    // 리렌더 시 innerHTML 을 다시 적용해, hydrate 가 정리한 차트
                    // 자리표시자(한 답변 한 차트)가 "부활"하던 문제의 방지책.
                    <RawHtml className="message-text" html={f.html} key={j} />
                  ) : f.kind === "chart" ? (
                    <div
                      className="chart-box"
                      key={j}
                      data-chart={encodeURIComponent(JSON.stringify(f.spec))}
                    />
                  ) : (
                    <RawHtml className={f.className} html={f.html} attrs={f.attrs} key={j} />
                  ),
                )}
              </div>
              {/* 실행 중 하단 로딩 도트 + 경과 시간 — 다음 텍스트까지 오래 걸려도
                  "멈춘 게 아니라 생각 중"임이 보이게. live 가 꺼지면 사라진다. */}
              {t.live && (
                <div className="stream-wait" aria-label="응답 생성 중">
                  <span className="stream-dot" />
                  <span className="stream-dot" />
                  <span className="stream-dot" />
                  {t.startedAt ? (
                    <span className="stream-wait-time">
                      <Elapsed since={t.startedAt} /> 경과
                    </span>
                  ) : null}
                </div>
              )}
              <div data-arts>
                {t.downloads.map((d, j) => (
                  <RawHtml className="art" key={j} html={d.html} quiet={d.quiet} />
                ))}
                {t.choices && t.choices.length > 0 && (
                  <div className="choice-box">
                    {t.choices.map((c, j) => (
                      <Chip
                        key={j}
                        variant="choice"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!busy) onPickChoice(i, c);
                        }}
                      >
                        {c}
                      </Chip>
                    ))}
                  </div>
                )}
                {t.nextFinal &&
                  t.next.length > 0 &&
                  !(t.choices && t.choices.length) && (
                    <div className="next-box">
                      <div className="next-label">이어서 물어보기</div>
                      <div className="next-row">
                        {t.next.slice(0, 5).map((q, j) => (
                          <Chip
                            key={j}
                            variant="next"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!busy) onPickNext(i, q);
                            }}
                          >
                            {q}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
              {/* 완료 후 메타 줄 — 답변 복사 + 완료 시각·총 소요 (예: 14:03 · 5분 28초 소요) */}
              {!t.live && <MsgMeta time={t.time} label="답변 복사" />}
            </div>
          </article>
        ),
      )}
    </div>
  );
}
